
const PENDING_KEY = "kre8:twitch:oauth";
let accessToken = null;
let connectionPromise = null;

export function getTwitchConfig() {
  return {
    clientId: String(process.env.REACT_APP_TWITCH_CLIENT_ID || "ivef38y1j2ej8awvh2qy2cj2un2x43").trim(),
    redirectUri: String(process.env.REACT_APP_TWITCH_REDIRECT_URI || `${window.location.origin}/stats`).trim(),
  };
}

export function startTwitchConnection() {
  const { clientId, redirectUri } = getTwitchConfig();
  if (!clientId) throw new Error("Twitch connection is not configured yet. Please try again later.");
  const redirect = new URL(redirectUri);
  if (redirect.origin !== window.location.origin || redirect.pathname !== "/stats" || redirect.hash || redirect.search) {
    throw new Error("The Twitch redirect URL must match this site's /stats page.");
  }
  disconnectTwitch();
  const state = Array.from(window.crypto.getRandomValues(new Uint8Array(32)), (byte) => byte.toString(16).padStart(2, "0")).join("");
  window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ state, createdAt: Date.now() }));
  const url = new URL("https://id.twitch.tv/oauth2/authorize");
  url.search = new URLSearchParams({ client_id: clientId, redirect_uri: redirectUri, response_type: "token", scope: "", state }).toString();
  window.location.assign(url.toString());
}

export function disconnectTwitch() {
  accessToken = null;
  connectionPromise = null;
  window.sessionStorage.removeItem(PENDING_KEY);
}

async function validateToken(token) {
  const response = await fetch("https://id.twitch.tv/oauth2/validate", { headers: { Authorization: `OAuth ${token}` } });
  if (!response.ok) {
    if (response.status === 401) disconnectTwitch();
    throw new Error(response.status === 401 ? "Your Twitch connection expired. Please connect again." : "Twitch could not validate your connection. Please try again.");
  }
  const identity = await response.json();
  if (!identity.user_id || identity.client_id !== getTwitchConfig().clientId || !(identity.expires_in > 0)) {
    disconnectTwitch();
    throw new Error("Twitch could not verify this connection. Please connect again.");
  }
  return identity;
}

// Consume and remove the OAuth fragment immediately. Tokens remain
// in memory only; refreshing the page requires reconnecting. No secret is used here.
export function completeTwitchConnection() {
  if (accessToken) return Promise.resolve(true);
  if (connectionPromise) return connectionPromise;
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const query = new URLSearchParams(window.location.search);
  const token = fragment.get("access_token");
  const callback = token || fragment.has("error") || query.has("error");
  if (!callback) return Promise.resolve(Boolean(accessToken));
  const params = token || fragment.has("error") ? fragment : query;
  const stored = window.sessionStorage.getItem(PENDING_KEY);
  window.sessionStorage.removeItem(PENDING_KEY);
  window.history.replaceState({}, "", "/stats?tab=twitch");
  connectionPromise = (async () => {
    let pending;
    try { pending = JSON.parse(stored); } catch { /* Invalid state is rejected below. */ }
    if (!pending?.state || params.get("state") !== pending.state || Date.now() - pending.createdAt > 600000 || !Number.isFinite(pending.createdAt)) {
      throw new Error("Twitch connection could not be verified. Please connect again.");
    }
    if (params.has("error")) throw new Error("Twitch connection was cancelled. You can try connecting again.");
    if (!token || params.get("token_type")?.toLowerCase() !== "bearer") throw new Error("Twitch did not return a valid connection. Please try again.");
    await validateToken(token);
    accessToken = token;
    return true;
  })();
  return connectionPromise;
}

export async function fetchTwitchChannelStats(user) {
  const token = accessToken;
  if (!token) throw new Error("Connect Twitch to load your channel stats.");
  const firebaseToken = await user?.getIdToken?.();
  if (!firebaseToken) throw new Error("Sign in to Kre8 to use the tracker.");
  const base = String(process.env.REACT_APP_ENDPOINT || "").replace(/\/$/, "");
  const response = await fetch(`${base}/api/stats/twitch`, { headers: { Authorization: `Bearer ${firebaseToken}`, "X-Twitch-Token": token } });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 409) disconnectTwitch();
    throw Object.assign(new Error(result.error || "Your broadcasts could not be loaded."), { status: response.status });
  }
  if (accessToken !== token) throw new Error("Connect Twitch to load your channel stats.");
  return result;
}

async function trackingRequest(channelId, days = 30, method = "GET") {
  if (!accessToken) throw new Error("Connect Twitch to manage tracking.");
  const base = String(process.env.REACT_APP_ENDPOINT || "").replace(/\/$/, "");
  const response = await fetch(`${base}/api/twitch/tracking/${encodeURIComponent(channelId)}?days=${days}`, {
    method, headers: { Authorization: `Bearer ${accessToken}` },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error || "Channel tracking could not be loaded.");
  return result;
}

export const fetchTwitchTracking = (channelId, days) => trackingRequest(channelId, days);
export const startTwitchTracking = (channelId) => trackingRequest(channelId, 30, "POST");
export const stopTwitchTracking = (channelId) => trackingRequest(channelId, 30, "DELETE");
